/**
 * @flow
 */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Container as BaseContainer, Menu as BaseMenu } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { get, find } from 'lodash';
import gql from 'graphql-tag';

import { PageHeader } from '../../../components';
import Tawk from '../../../providers/Tawk';
import CreateAppointmentButton from '../../appointments/components/CreateAppointmentButton';
import AppointmentTable from './AppointmentTable';
import DateRange from './DateRange';
import BaseMasterSelect from '../containers/MasterSelect';
import MasterInfoButton from './MasterInfoButton';
import GenerateScheduleButton from '../../settings/components/schedule/GenerateScheduleButton';

const GET_MASTERS_QUERY = gql`
    query getMasters($salon: ID!, $lang: String!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    masters {
                        nodes {
                            id
                            firstName
                            lastName
                            phone
                            email
                            status
                            type
                            serviceGroups {
                                id
                                name(lang: $lang)
                            }
                        }
                    }
                }
            }
        }
    }
`;

const Container = styled(BaseContainer)`
    &.container {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
`;

const Menu = styled(BaseMenu)`
    &.menu {
        margin: -0.5em -1em !important;
    }
`;

const TimetableWrapper = styled.div`
    margin: 0.5em -2em 0 -2em;
    flex: 1;
`;

const MasterSelect = styled(BaseMasterSelect)`
    .ui.selection.dropdown& {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
`;

function MasterAppointmentsPage() {
    const master = useSelector(({ masterAppointments }) =>
        masterAppointments.get('master'),
    );
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    useEffect(() => {
        Tawk.minimizeTawk();
    }, []);

    const { data } = useQuery(GET_MASTERS_QUERY, {
        variables: { salon, lang: currentLanguage },
        fetchPolicy: 'cache-and-network',
        skip: !salon,
    });

    const masters = get(data, 'viewer.salon.masters.nodes', []);
    const currentMaster = master && find(masters, m => m.id === master.id);

    return (
        <Container fluid>
            <Menu secondary>
                <BaseMenu.Item header>
                    <PageHeader>
                        <FormattedMessage
                            id="pages.masterAppointments.header"
                            defaultMessage="Master Appointments"
                        />
                    </PageHeader>
                </BaseMenu.Item>
                <BaseMenu.Menu position="right">
                    <BaseMenu.Item>
                        <GenerateScheduleButton master={currentMaster} />
                    </BaseMenu.Item>
                    <BaseMenu.Item>
                        <MasterSelect masters={masters} />
                        {currentMaster && (
                            <MasterInfoButton master={currentMaster} />
                        )}
                    </BaseMenu.Item>
                    <BaseMenu.Item>
                        <DateRange />
                    </BaseMenu.Item>
                    <BaseMenu.Item>
                        <CreateAppointmentButton
                            master={currentMaster && currentMaster.id}
                            disabled={!currentMaster || !currentMaster.id}
                        />
                    </BaseMenu.Item>
                </BaseMenu.Menu>
            </Menu>
            <TimetableWrapper>
                <AppointmentTable />
            </TimetableWrapper>
        </Container>
    );
}

export default MasterAppointmentsPage;
